import React, { useEffect, useRef, useState } from "react";
import { read, utils } from "xlsx";
import canvasDatagrid from "canvas-datagrid";
import Loader from "@shared/components/Loader";
import { Tooltip } from "flowbite-react";

interface Anomaly {
  column_name: string;
  sheet_name: string;
  ranges: [number, number][];
  total_affected: number;
}

interface ExcelPreviewCanvasProps {
  file: Blob;
  anomalyData?: Anomaly[];
}

const CHUNK_SIZE = 200;

type HighlightMode =
  | "cell"
  | "col"
  | "row"
  | "multiple"
  | "value"
  | "valueBetween"
  | null;

const ExcelPreviewCanvas: React.FC<ExcelPreviewCanvasProps> = ({
  file,
  anomalyData = [],
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstance = useRef<any | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [workbook, setWorkbook] = useState<any>(null);
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [fullRows, setFullRows] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetReady, setSheetReady] = useState(false);

  const highlightTargetRef = useRef<{
    sheet: string;
    mode: HighlightMode;
    data: any;
  } | null>(null);

  const columnIndexMap = useRef<Record<string, number>>({});

  const getGridFont = () => {
    if (window.innerWidth < 640) return "9px Inter, sans-serif";
    if (window.innerWidth < 1024) return "11px Inter, sans-serif";
    if (window.innerWidth < 1600) return "13px Inter, sans-serif";
    return "13px Inter, sans-serif"; // 2xl+
  };

  const renderGrid = (rows: any[]) => {
    if (gridRef.current) gridRef.current.innerHTML = "";

    const grid = canvasDatagrid({
      parentNode: gridRef.current!,
      data: rows,
      readOnly: true,
      showNewRow: false,
      autoResize: true,
    });

    grid.style.width = "100%";
    grid.style.height = "100%";
    grid.style.font = getGridFont();

    grid.addEventListener("cellmousedown", (e: any) => e.preventDefault());
    grid.addEventListener("beforerendercell", (e: any) => {
      e.cell.editable = false;
    });
    grid.addEventListener("dblclick", (e: Event) => e.preventDefault());
    grid.editCellAt = () => false;
    grid.addEventListener("keydown", (e: KeyboardEvent) => e.preventDefault());

    grid.addEventListener("rendercell", function (e: any) {
      const target = highlightTargetRef.current;
      if (!target || target.sheet !== activeSheet) return;

      const { rowIndex, columnIndex } = e.cell;
      let shouldHighlight = false;

      switch (target.mode) {
        case "multiple":
          shouldHighlight = target.data.some(
            (cell: any) => cell.row === rowIndex && cell.col === columnIndex
          );
          break;
      }

      if (shouldHighlight) {
        e.ctx.fillStyle = "#FFF9B1";
        e.ctx.fillRect(e.cell.x, e.cell.y, e.cell.width, e.cell.height);
      }
    });

    gridInstance.current = grid;
    requestAnimationFrame(() => {
      grid.resize();
      grid.draw();
      setLoading(false);
    });
  };

  const scrollToCell = async (row: number, col: number) => {
    const required = row + 1;
    if (visibleRows.length < required) {
      const end = Math.min(fullRows.length, required);
      const updated = fullRows.slice(0, end);
      setVisibleRows(updated);
      gridInstance.current.data = updated;
      gridInstance.current.draw();
    }

    gridInstance.current.scrollIntoView(col, row);
  };

  const highlightAffectedCells = async (
    sheet: string,
    anomalies: Anomaly[]
  ) => {
    const relevantAnomalies = anomalies.filter(
      (anomaly) => anomaly.sheet_name === sheet
    );

    const cellCoords: { row: number; col: number }[] = [];

    relevantAnomalies.forEach(({ column_name, ranges }) => {
      const col = columnIndexMap.current[column_name];
      if (col === undefined) return;

      ranges.forEach(([start, end]) => {
        if (start === 0 && end === -1) {
          // full column case
          for (let row = 0; row < fullRows.length; row++) {
            cellCoords.push({ row, col });
          }
        } else {
          for (let row = start; row <= end; row++) {
            cellCoords.push({ row, col });
          }
        }
      });
    });

    if (cellCoords.length) {
      highlightTargetRef.current = {
        sheet,
        mode: "multiple",
        data: cellCoords,
      };
      gridInstance.current?.draw();
    }
  };

  const jumpToAnomaly = async (columnName: string, sheetName: string) => {
    const anomaly = anomalyData.find(
      (a) => a.column_name === columnName && a.sheet_name === sheetName
    );
    const col = columnIndexMap.current[columnName];
    if (!anomaly || col === undefined) return;

    const firstRange = anomaly.ranges[0];
    const row = firstRange?.[0] ?? 0;

    if (sheetName !== activeSheet) {
      setActiveSheet(sheetName);
    }

    await scrollToCell(row, col);
  };

  const parseSheet = (sheetName: string) => {
    setLoading(true);

    setTimeout(() => {
      const sheet = workbook.Sheets[sheetName];
      const json = utils.sheet_to_json(sheet, { defval: "", header: 1 });

      const rawHeaders = json[0] as string[];
      const allRows = json.slice(1).map((row: any) => {
        const rowData: any = {};
        rawHeaders.forEach((h: string, i: number) => {
          rowData[h || `Col ${i + 1}`] = row[i] ?? "";
        });
        return rowData;
      });

      rawHeaders.forEach((header, i) => {
        columnIndexMap.current[header] = i;
      });

      setHeaders(rawHeaders);
      setFullRows(allRows);
      setVisibleRows(allRows.slice(0, CHUNK_SIZE));
      setSheetReady(true);

      requestAnimationFrame(() => {
        renderGrid(allRows.slice(0, CHUNK_SIZE));
      });
    }, 50);
  };

  const watchCanvasChanges = () => {
    const canvasHost = document.querySelector("canvas-datagrid") as HTMLElement;
    if (!canvasHost?.shadowRoot) return;
    const canvasElement = canvasHost.shadowRoot.querySelector("canvas");
    if (!canvasElement) return;

    const observer = new MutationObserver(() => {
      setLoading(false);
      observer.disconnect();
    });

    observer.observe(canvasElement, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const updateActiveSheet = (name: string) => {
    setActiveSheet(name);
    document.querySelector(".canvas-datagrid-edit-input")?.remove();
  };

  useEffect(() => {
    const loadWorkbook = async () => {
      const buffer = await file.arrayBuffer();
      const wb = read(buffer, { type: "array" });
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      setActiveSheet(wb.SheetNames[0]);
    };

    loadWorkbook();
  }, [file]);

  useEffect(() => {
    if (activeSheet && workbook) {
      parseSheet(activeSheet);
    }
  }, [activeSheet, workbook]);

  useEffect(() => {
    if (sheetReady && anomalyData.length) {
      highlightAffectedCells(activeSheet, anomalyData);
    }
  }, [sheetReady, anomalyData, activeSheet]);

  useEffect(() => {
    return () => {
      setFullRows([]);
      setVisibleRows([]);
      setWorkbook(null);
      setSheetNames([]);
      setActiveSheet("");
      setHeaders([]);
      setSheetReady(false);
      setLoading(true);

      if (gridInstance.current) {
        gridRef.current?.replaceChildren();
        document.querySelector(".canvas-datagrid-edit-input")?.remove();
        gridInstance.current = null;
      }
    };
  }, []);

  return (
    <div className="h-full w-full flex flex-col">
      {/* Sheet Tabs */}
      <div className="flex p-2 gap-2 border-b border-gray-300">
        {sheetNames.map((name) => {
          const hasAnomalies = anomalyData.some((a) => a.sheet_name === name);
          return (
            <button
              type="button"
              key={name}
              onClick={() => updateActiveSheet(name)}
              className={`px-3 py-1 border-b-2 flex items-center gap-1 ${
                activeSheet === name
                  ? "border-blue-600 font-bold"
                  : "border-transparent font-normal"
              } text-xs 2xl:text-sm`}
            >
              {name}
              {hasAnomalies && (
                <Tooltip
                  content="This sheet has anomalies"
                  placement="top"
                  arrow={false}
                >
                  <span className="text-yellow-500 cursor-pointer">⚠️</span>
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>

      {/* Anomaly Links */}
      {!!anomalyData.length &&
        anomalyData.some((a) => a.sheet_name === activeSheet) && (
          <div className="h-10">
            {!loading && (
              <div className="p-2 bg-yellow-50 border-b h-10 items-center border-yellow-200 text-xs 2xl:text-sm flex gap-2 flex-wrap">
                <span className="font-semibold text-yellow-700">
                  View Anomalies In:
                </span>
                {anomalyData
                  .filter((a) => a.sheet_name === activeSheet)
                  .map((a, i) => (
                    <button
                      key={`${a.sheet_name}-${a.column_name}-${i}`}
                      type="button"
                      onClick={() => jumpToAnomaly(a.column_name, a.sheet_name)}
                      className="underline text-blue-600 hover:text-blue-800 transition"
                    >
                      {a.column_name}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

      {/* Grid */}
      <div className="flex-1 overflow-hidden relative mb-4">
        <div ref={gridRef} className="h-full w-full" />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
            <Loader color="#2462EB" size="lg" />
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="h-[80px]">
        {!loading && visibleRows.length < fullRows.length && (
          <div className="p-3 text-center">
            <button
              type="button"
              onClick={() => {
                const nextChunk = fullRows.slice(
                  visibleRows.length,
                  visibleRows.length + CHUNK_SIZE
                );
                const updated = [...visibleRows, ...nextChunk];
                setVisibleRows(updated);
                if (gridInstance.current) {
                  gridInstance.current.data = updated;
                  gridInstance.current.draw();
                }
              }}
              className="px-4 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Load more rows
            </button>
            <div className="text-xs text-gray-500 mt-1">
              Showing {visibleRows.length} of {fullRows.length} rows.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelPreviewCanvas;
